// ---------------------------------------------------------------------------
// CSS Cascade Builder
// ---------------------------------------------------------------------------
// Builds the <style> block with 3 layers:
//   1. Base reset (box-sizing, body defaults, utility classes)
//   2. CSS custom properties from site globalTheme
//   3. Site custom CSS + page custom CSS

import type { SiteTheme } from "../schema/index.js";
import { sanitizeCss } from "../security/css-sanitizer.js";
import { resolveSiteTheme } from "../theme/index.js";
import { ANIMATION_CSS } from "./animation.js";
import {
  SIMPLE_EDITOR_DEFAULTS,
  SIMPLE_EDITOR_TYPE_SCALE,
} from "../defaults/smart-defaults.js";
import {
  buildPublishedMoodVariables,
  type MoodId,
} from "../dss/mood-themes.js";

// ---------------------------------------------------------------------------
// Base reset — minimal, opinionated for landing pages
// ---------------------------------------------------------------------------

const BASE_RESET = `*,*::before,*::after{box-sizing:border-box}
body{margin:0;font-family:var(--ease-font-body,system-ui,-apple-system,sans-serif);color:var(--ease-color-text,#1f2937);background:var(--ease-color-bg,#fff);line-height:1.6}
img,video{max-width:100%;height:auto;display:block}
input,select,textarea,button{font:inherit}
.ease-section{padding:var(--ease-spacing-section,${SIMPLE_EDITOR_DEFAULTS.sectionSpacing}) 1rem}
.ease-container{max-width:1200px;margin:0 auto;padding:0 1rem}
.ease-btn{display:inline-block;padding:.75rem 1.5rem;border-radius:var(--ease-button-radius,${SIMPLE_EDITOR_DEFAULTS.buttonRadius});text-decoration:none;font-weight:600;cursor:pointer;border:1px solid var(--ease-button-border,transparent);box-shadow:var(--ease-button-shadow,none);font-size:var(--ease-font-size-body,${SIMPLE_EDITOR_TYPE_SCALE.body});transition:opacity .2s,transform .2s,box-shadow .2s}
.ease-btn:hover{opacity:.95;transform:translateY(-1px)}
.ease-btn--primary{background:var(--ease-button-primary-bg,var(--ease-color-brand,#2563eb));color:var(--ease-button-primary-text,#fff)}
.ease-btn--secondary{background:var(--ease-button-secondary-bg,var(--ease-color-surface,#f3f4f6));color:var(--ease-button-secondary-text,var(--ease-color-text,#1f2937))}
.ease-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.ease-skip-link{position:absolute;top:-100%;left:0;background:#000;color:#fff;padding:.5rem 1rem;z-index:9999;text-decoration:none}
.ease-skip-link:focus{top:0}
:where(a,button,input,select,textarea,summary,[role="button"],[role="link"],[tabindex]:not([tabindex="-1"])):focus-visible{outline:3px solid var(--ease-focus-ring,var(--ease-color-brand,#2563eb));outline-offset:3px}
h1,h2,h3,h4,h5,h6{font-family:var(--ease-font-heading,var(--ease-font-body,system-ui,sans-serif));line-height:1.2;margin:0 0 .5em}
h1{font-size:var(--ease-font-size-h1,${SIMPLE_EDITOR_TYPE_SCALE.h1})}
h2{font-size:var(--ease-font-size-h2,${SIMPLE_EDITOR_TYPE_SCALE.h2})}
h3{font-size:var(--ease-font-size-h3,${SIMPLE_EDITOR_TYPE_SCALE.h3})}
p{margin:0 0 1em;font-size:var(--ease-font-size-body,${SIMPLE_EDITOR_TYPE_SCALE.body})}
a{color:var(--ease-color-brand,#2563eb)}
.ease-site-header,.ease-site-footer{background:var(--ease-color-surface,var(--ease-color-bg,#fff));border-bottom:1px solid var(--ease-card-border,var(--ease-color-muted,#e5e7eb))}
.ease-site-footer{border-bottom:none;border-top:1px solid var(--ease-card-border,var(--ease-color-muted,#e5e7eb))}
.ease-nav-list,.ease-footer-list{display:flex;flex-wrap:wrap;gap:1rem;list-style:none;margin:0;padding:1rem}
.ease-comparison-check{color:var(--ease-color-success,#16a34a)}
.ease-comparison-cross{color:var(--ease-color-danger,#dc2626)}
@keyframes ease-logo-scroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
.ease-logo-cloud-track{display:flex;animation:ease-logo-scroll 20s linear infinite;width:max-content}
.ease-logo-cloud-track:hover{animation-play-state:paused}`;

// ---------------------------------------------------------------------------
// CSS value sanitization
// ---------------------------------------------------------------------------
// CSS values injected into <style> blocks are a breakout risk.
// We validate against strict patterns: no semicolons, braces, angle brackets,
// or any character that could close a CSS declaration or inject HTML.

/** Characters that should never appear in CSS dimension or font values */
const CSS_BREAKOUT_CHARS = /[;{}<>\\"`]/;

/** Matches hex (#fff, #aabbcc, #aabbccdd), rgb/rgba/hsl/hsla, named colors */
const CSS_COLOR_PATTERN = /^(#[0-9a-fA-F]{3,8}|rgba?\(\s*[\d.,\s%]+\)|hsla?\(\s*[\d.,\s%deg]+\)|[a-zA-Z]{2,30}|transparent|currentColor|inherit)$/;

/** Matches CSS dimension values (8px, 1rem, 0.5em, 100%, auto, none) */
const CSS_DIMENSION_PATTERN = /^(\d+(\.\d+)?(px|rem|em|%|vh|vw|ch|ex)|auto|none|inherit|0)$/;

/** Matches safe font family names (letters, numbers, spaces, hyphens) */
const CSS_FONT_FAMILY_PATTERN = /^[a-zA-Z0-9 \-]+$/;

interface RgbColor {
  red: number;
  green: number;
  blue: number;
}

function sanitizeCssColor(value: string): string | null {
  const trimmed = value.trim();
  // Color patterns are validated by regex which only allows safe chars
  if (!CSS_COLOR_PATTERN.test(trimmed)) return null;
  return trimmed;
}

function sanitizeThemeTokenColor(value: string): string | null {
  const trimmed = value.trim();
  if (sanitizeCssColor(trimmed)) return trimmed;
  if (CSS_BREAKOUT_CHARS.test(trimmed)) return null;
  return /^var\(--[a-zA-Z0-9_-]+(?:\s*,\s*[-#(),.%\w\s]+)?\)$/.test(trimmed)
    ? trimmed
    : null;
}

function sanitizeCssDimension(value: string): string | null {
  const trimmed = value.trim();
  if (CSS_BREAKOUT_CHARS.test(trimmed)) return null;
  if (!CSS_DIMENSION_PATTERN.test(trimmed)) return null;
  return trimmed;
}

function sanitizeFontFamily(value: string): string | null {
  const trimmed = value.trim();
  if (!CSS_FONT_FAMILY_PATTERN.test(trimmed)) return null;
  return trimmed;
}

function parseColorToRgb(value: string | undefined): RgbColor | null {
  if (!value) return null;

  const trimmed = value.trim();
  const hex = trimmed.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (hex) {
    const hexValue = hex[1];
    if (!hexValue) return null;

    const normalized = hexValue.length === 3
      ? hexValue.split("").map((char) => `${char}${char}`).join("")
      : hexValue;
    return {
      red: Number.parseInt(normalized.slice(0, 2), 16),
      green: Number.parseInt(normalized.slice(2, 4), 16),
      blue: Number.parseInt(normalized.slice(4, 6), 16),
    };
  }

  const rgb = trimmed.match(/^rgba?\(([^)]+)\)$/i);
  if (!rgb) return null;

  const rgbBody = rgb[1];
  if (!rgbBody) return null;

  const parts = rgbBody.split(",").map((part) => Number.parseFloat(part.trim()));
  if (parts.length < 3 || parts.slice(0, 3).some((part) => Number.isNaN(part))) {
    return null;
  }

  return {
    red: Math.max(0, Math.min(255, parts[0]!)),
    green: Math.max(0, Math.min(255, parts[1]!)),
    blue: Math.max(0, Math.min(255, parts[2]!)),
  };
}

function toLinearChannel(value: number): number {
  const normalized = value / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

function getRelativeLuminance(color: RgbColor): number {
  return (0.2126 * toLinearChannel(color.red))
    + (0.7152 * toLinearChannel(color.green))
    + (0.0722 * toLinearChannel(color.blue));
}

function getContrastRatio(background: RgbColor, foreground: RgbColor): number {
  const lighter = Math.max(getRelativeLuminance(background), getRelativeLuminance(foreground));
  const darker = Math.min(getRelativeLuminance(background), getRelativeLuminance(foreground));
  return (lighter + 0.05) / (darker + 0.05);
}

function pickAccessibleTextColor(background: string | undefined): string | null {
  const backgroundRgb = parseColorToRgb(background);
  if (!backgroundRgb) return null;

  const white = { red: 255, green: 255, blue: 255 };
  const dark = { red: 31, green: 41, blue: 55 };

  return getContrastRatio(backgroundRgb, white) >= getContrastRatio(backgroundRgb, dark)
    ? "#ffffff"
    : "#1f2937";
}

// ---------------------------------------------------------------------------
// CSS variable builder
// ---------------------------------------------------------------------------

export function buildCssVariables(theme: SiteTheme): string {
  const resolvedTheme = resolveSiteTheme(theme);
  const vars: string[] = [];

  const colorEntries: Array<[string, string | undefined]> = [
    ["--ease-color-brand", resolvedTheme.colors?.brand],
    ["--ease-color-brand-dark", resolvedTheme.colors?.brandDark],
    ["--ease-color-secondary", resolvedTheme.colors?.secondary],
    ["--ease-color-accent", resolvedTheme.colors?.accent],
    ["--ease-color-text", resolvedTheme.colors?.text],
    ["--ease-color-bg", resolvedTheme.colors?.background],
    ["--ease-color-surface", resolvedTheme.colors?.surface],
    ["--ease-color-muted", resolvedTheme.colors?.muted],
  ];

  for (const [prop, value] of colorEntries) {
    if (!value) continue;
    const safe = sanitizeCssColor(value);
    if (safe) vars.push(`${prop}:${safe}`);
  }

  vars.push("--ease-color-border:var(--ease-color-muted,#e5e7eb)");
  vars.push("--ease-color-text-soft:var(--ease-color-secondary,var(--ease-color-text,#475569))");
  vars.push("--ease-color-surface-muted:var(--ease-color-surface,#f8fafc)");
  vars.push("--ease-color-success:#16a34a");
  vars.push("--ease-color-danger:#dc2626");
  vars.push("--ease-color-warning:var(--ease-color-accent,#f59e0b)");
  vars.push(`--ease-font-size-body:${SIMPLE_EDITOR_TYPE_SCALE.body}`);
  vars.push(`--ease-font-size-h3:${SIMPLE_EDITOR_TYPE_SCALE.h3}`);
  vars.push(`--ease-font-size-h2:${SIMPLE_EDITOR_TYPE_SCALE.h2}`);
  vars.push(`--ease-font-size-h1:${SIMPLE_EDITOR_TYPE_SCALE.h1}`);
  vars.push(`--ease-card-radius:var(--ease-radius,${SIMPLE_EDITOR_DEFAULTS.cardRadius})`);
  vars.push("--ease-card-padding:1.5rem");
  vars.push("--ease-overlay-backdrop:rgba(15,23,42,.45)");

  if (resolvedTheme.fonts?.heading) {
    const safe = sanitizeFontFamily(resolvedTheme.fonts.heading.family);
    if (safe) vars.push(`--ease-font-heading:'${safe}',sans-serif`);
  }
  if (resolvedTheme.fonts?.body) {
    const safe = sanitizeFontFamily(resolvedTheme.fonts.body.family);
    if (safe) vars.push(`--ease-font-body:'${safe}',sans-serif`);
  }

  if (resolvedTheme.borderRadius) {
    const safe = sanitizeCssDimension(resolvedTheme.borderRadius);
    if (safe) vars.push(`--ease-radius:${safe}`);
  }
  if (resolvedTheme.spacing) {
    const safe = sanitizeCssDimension(resolvedTheme.spacing);
    if (safe) {
      vars.push(`--ease-spacing:${safe}`);
      vars.push(`--ease-spacing-section:${safe}`);
    }
  } else {
    vars.push(`--ease-spacing:${SIMPLE_EDITOR_DEFAULTS.sectionSpacing}`);
    vars.push(`--ease-spacing-section:${SIMPLE_EDITOR_DEFAULTS.sectionSpacing}`);
  }

  if (resolvedTheme.buttonStyle === "soft") {
    vars.push("--ease-button-primary-bg:var(--ease-color-surface,var(--ease-color-bg,#fff))");
    vars.push(`--ease-button-primary-text:${pickAccessibleTextColor(resolvedTheme.colors?.surface ?? resolvedTheme.colors?.background) ?? "var(--ease-color-brand,#2563eb)"}`);
    vars.push("--ease-button-border:var(--ease-color-muted,#e5e7eb)");
    vars.push("--ease-button-shadow:none");
  } else if (resolvedTheme.buttonStyle === "outline") {
    vars.push("--ease-button-primary-bg:transparent");
    vars.push("--ease-button-primary-text:var(--ease-color-brand,#2563eb)");
    vars.push("--ease-button-border:var(--ease-color-brand,#2563eb)");
    vars.push("--ease-button-shadow:none");
  } else if (resolvedTheme.buttonStyle === "ghost") {
    vars.push("--ease-button-primary-bg:transparent");
    vars.push("--ease-button-primary-text:var(--ease-color-brand,#2563eb)");
    vars.push("--ease-button-border:transparent");
    vars.push("--ease-button-shadow:none");
  } else {
    vars.push("--ease-button-primary-bg:var(--ease-color-brand,#2563eb)");
    vars.push(`--ease-button-primary-text:${pickAccessibleTextColor(resolvedTheme.colors?.brand) ?? "#fff"}`);
    vars.push("--ease-button-border:transparent");
    vars.push("--ease-button-shadow:0 10px 24px rgba(15,23,42,.12)");
  }

  vars.push("--ease-button-secondary-bg:var(--ease-color-surface,#f3f4f6)");
  vars.push(`--ease-button-secondary-text:${pickAccessibleTextColor(resolvedTheme.colors?.surface ?? resolvedTheme.colors?.background) ?? "var(--ease-color-text,#1f2937)"}`);
  vars.push(`--ease-button-radius:${SIMPLE_EDITOR_DEFAULTS.buttonRadius}`);

  if (resolvedTheme.cardStyle === "outline") {
    vars.push("--ease-card-border:var(--ease-color-muted,#d1d5db)");
    vars.push("--ease-card-shadow:none");
  } else if (resolvedTheme.cardStyle === "shadow") {
    vars.push("--ease-card-border:transparent");
    vars.push(`--ease-card-shadow:${SIMPLE_EDITOR_DEFAULTS.cardShadow}`);
  } else if (resolvedTheme.cardStyle === "elevated") {
    vars.push("--ease-card-border:transparent");
    vars.push("--ease-card-shadow:0 20px 45px rgba(15,23,42,.18)");
  } else {
    vars.push("--ease-card-border:transparent");
    vars.push("--ease-card-shadow:none");
  }

  return vars.length > 0 ? `:root{${vars.join(";")}}` : "";
}

export function buildMoodCssVariables(moodId: MoodId | undefined): string {
  if (!moodId) return "";

  const moodVariables = buildPublishedMoodVariables(moodId);
  const vars: string[] = [];

  for (const [prop, value] of Object.entries(moodVariables)) {
    const safe = sanitizeThemeTokenColor(value);
    if (safe) {
      vars.push(`${prop}:${safe}`);
    }
  }

  return vars.length > 0 ? `:root{${vars.join(";")}}` : "";
}

// Exported for testing
export {
  sanitizeCssColor,
  sanitizeCssDimension,
  sanitizeFontFamily,
  sanitizeThemeTokenColor,
};

// ---------------------------------------------------------------------------
// Full style block builder
// ---------------------------------------------------------------------------

export function buildStyleBlock(
  siteTheme: SiteTheme | undefined,
  siteCustomCss: string | undefined,
  pageCustomCss: string | undefined,
  moodId?: MoodId,
): string {
  const parts: string[] = [BASE_RESET, ANIMATION_CSS];

  if (siteTheme) {
    const cssVars = buildCssVariables(siteTheme);
    if (cssVars) parts.push(cssVars);
  }

  const moodCssVars = buildMoodCssVariables(moodId);
  if (moodCssVars) {
    parts.push(moodCssVars);
  }

  const sanitizedSiteCustomCss = siteCustomCss?.trim()
    ? sanitizeCss(siteCustomCss).trim()
    : "";
  if (sanitizedSiteCustomCss) {
    parts.push(sanitizedSiteCustomCss);
  }

  const sanitizedPageCustomCss = pageCustomCss?.trim()
    ? sanitizeCss(pageCustomCss).trim()
    : "";
  if (sanitizedPageCustomCss) {
    parts.push(sanitizedPageCustomCss);
  }

  return `<style>${parts.join("\n")}</style>`;
}
